import React, { useMemo, useState } from 'react';
import { Box, Typography, IconButton, Divider, InputBase } from '@mui/material';
import { Add, Remove } from '@mui/icons-material';

const fmt = (n) => `₹${parseFloat(n || 0).toFixed(2)}`;

const DenominationRow = ({ denomination, count, onChange }) => {
  const subtotal = (denomination.value || 0) * count;
  // Text being typed; null means show the committed count. Lets the field be emptied mid-edit.
  const [draft, setDraft] = useState(null);

  const handleType = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
    setDraft(digits);
    onChange(denomination.id, (digits === '' ? 0 : parseInt(digits, 10)) - count);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, sm: 1.5 },
        px: { xs: 1, sm: 1.5 },
        py: { xs: 0.75, sm: 1 },
        borderBottom: '1px solid #E2E8F0',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      {/* Denomination label */}
      <Box sx={{ minWidth: { xs: 56, sm: 72 }, flexShrink: 0 }}>
        <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' }, fontWeight: 700, color: '#1E293B', lineHeight: 1.2 }}>
          ₹{denomination.value}
        </Typography>
        <Typography sx={{ fontSize: '0.6rem', color: '#94A3B8', lineHeight: 1.2 }} noWrap>
          {denomination.label}
        </Typography>
      </Box>

      {/* Stepper */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          bgcolor: '#F8FAFC',
          border: '1px solid #E2E8F0',
          borderRadius: '8px',
          overflow: 'hidden',
          flexShrink: 0,
          ml: { xs: 0.5, sm: 1 },
        }}
      >
        <IconButton
          size="small"
          onClick={() => { setDraft(null); onChange(denomination.id, -1); }}
          disabled={count <= 0}
          sx={{ borderRadius: 0, px: { xs: 0.75, sm: 1.25 }, color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}
        >
          <Remove sx={{ fontSize: { xs: 14, sm: 16 } }} />
        </IconButton>
        <InputBase
          value={draft ?? count}
          onChange={handleType}
          onFocus={(e) => e.target.select()}
          onBlur={() => setDraft(null)}
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
            'aria-label': `Count of ₹${denomination.value}`,
            style: { textAlign: 'center', padding: 0 },
          }}
          sx={{ width: { xs: 44, sm: 56 }, fontWeight: 800, fontSize: { xs: '0.8rem', sm: '0.9rem' }, color: '#1E293B' }}
        />
        <IconButton
          size="small"
          onClick={() => { setDraft(null); onChange(denomination.id, 1); }}
          sx={{ borderRadius: 0, px: { xs: 0.75, sm: 1.25 }, color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}
        >
          <Add sx={{ fontSize: { xs: 14, sm: 16 } }} />
        </IconButton>
      </Box>

      {/* Subtotal */}
      <Typography
        sx={{
          flexGrow: 1,
          textAlign: 'right',
          fontSize: { xs: '0.78rem', sm: '0.85rem' },
          fontWeight: count > 0 ? 700 : 500,
          color: count > 0 ? '#1E293B' : '#94A3B8',
        }}
      >
        {fmt(subtotal)}
      </Typography>
    </Box>
  );
};

const DenominationCountGrid = ({ denominations = [], counts = {}, onChange, totalLabel = 'Total' }) => {
  const total = useMemo(
    () => denominations.reduce((sum, d) => sum + (d.value || 0) * (counts[d.id] || 0), 0),
    [denominations, counts]
  );

  return (
    <Box>
      <Typography
        sx={{
          fontSize: { xs: '0.6rem', sm: '0.65rem' },
          fontWeight: 900,
          letterSpacing: '0.05em',
          color: '#94A3B8',
          mb: 0.75,
        }}
      >
        DENOMINATION COUNT
      </Typography>

      <Box
        sx={{
          border: '1px solid #E2E8F0',
          borderRadius: '8px',
          bgcolor: '#FFF',
          maxHeight: { xs: 260, sm: 290, md: 280 },
          overflowY: 'auto',
          scrollbarWidth: 'thin',
          scrollbarColor: '#CBD5E1 transparent',
          '&::-webkit-scrollbar': { width: '5px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: '#CBD5E1', borderRadius: '3px' },
          '&::-webkit-scrollbar-thumb:hover': { background: '#94A3B8' },
        }}
      >
        {denominations.map((d) => (
          <DenominationRow key={d.id} denomination={d} count={counts[d.id] || 0} onChange={onChange} />
        ))}
      </Box>

      <Divider sx={{ my: 1 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: { xs: 0.5, sm: 1 } }}>
        <Typography sx={{ fontSize: { xs: '0.78rem', sm: '0.85rem' }, fontWeight: 800, color: '#1E293B', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
          {totalLabel}
        </Typography>
        <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, fontWeight: 800, color: '#1E293B' }}>
          {fmt(total)}
        </Typography>
      </Box>
    </Box>
  );
};

export { fmt };
export default DenominationCountGrid;
